import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Stack,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoIcon from "@mui/icons-material/Info";
import { createRenew } from "../../../Services/RenewTanentPlaneApi";

/*
  RenewSystemDialog props:
  - open: boolean
  - onClose: () => void
  - tenantId: string (prefill tenant_id)
  - defaults: optional object to prefill other fields
  - onSuccess: (response) => void   // called after successful createRenew
*/
const RenewSystemDialog = ({ open, onClose, tenantId, defaults = {}, onSuccess }) => {
  const [form, setForm] = useState({
    tenant_id: tenantId || "",
    cost: defaults.cost || "",
    renew_date: defaults.renew_date || "",
    start_date: defaults.start_date || "",
    end_date: defaults.end_date || "",
    employee_name: defaults.employee_name || "",
    notes: defaults.notes || "",
    file: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // عند تغيير tenantId أو defaults أو فتح الحوار، عبئ الحقول بالقيم الافتراضية
  useEffect(() => {
    if (!open) return;
    setForm({
      tenant_id: tenantId || "",
      cost: defaults.cost ?? "",
      renew_date: defaults.renew_date ?? "",
      start_date: defaults.start_date ?? "",
      end_date: defaults.end_date ?? "",
      employee_name: defaults.employee_name ?? "",
      notes: defaults.notes ?? "",
      file: null,
    });
  }, [tenantId, defaults, open]);

  // مساعد بسيط لعرض التواريخ بصيغة قصيرة
  const formatShortDate = (d) => {
    if (!d) return "-";
    try {
      const dt = new Date(d);
      return dt.toLocaleDateString("ar-EG");
    } catch {
      return d;
    }
  };

  const handleChange = (key) => (e) => {
    const value = key === "file" ? e.target.files?.[0] ?? null : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (!form.tenant_id) return "معرف المستأجر مطلوب.";
    if (!form.cost) return "التكلفة مطلوبة.";
    if (!form.renew_date) return "تاريخ التجديد مطلوب.";
    return null;
  };

  const handleSubmit = async () => {
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setLoading(true);
    try {
      // بيانات تُرسل إلى createRenew ككائن (الدالة ستبني FormData)
      const payload = {
        tenant_id: form.tenant_id,
        cost: form.cost,
        renew_date: form.renew_date,
        start_date: form.start_date,
        end_date: form.end_date,
        employee_name: form.employee_name,
        notes: form.notes,
      };
      if (form.file) payload.file = form.file;

      const res = await createRenew(payload);
      // مرّر الاستجابة و payload إلى المستدعي ليتمكن من استلام القيم (cost, start_date, end_date)
      if (onSuccess) onSuccess(res, payload);
      // اغلاق ونظافة
      setForm({
        tenant_id: tenantId || "",
        cost: "",
        renew_date: "",
        start_date: "",
        end_date: "",
        employee_name: "",
        notes: "",
        file: null,
      });
      onClose && onClose();
    } catch (err) {
      console.error("createRenew error:", err);
      const msg = err?.response?.data?.message || err.message || "فشل إنشاء التجديد";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={Boolean(open)} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(255, 106, 0, 0.2)",
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)",
          color: "#fff",
          py: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          ✨ إنشاء تجديد جديد
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, background: "#fafafa" }}>
        {/* عرض القيم السابقة إن كانت موجودة */}
        {(defaults?.cost || defaults?.start_date || defaults?.end_date) && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: 2, 
              background: "linear-gradient(135deg, #fff5e6 0%, #ffe0b2 100%)",
              border: "1px solid #ff9a56"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <InfoIcon sx={{ color: "#ff6a00", fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#ff6a00" }}>
                📋 القيم السابقة
              </Typography>
            </Box>
            <Divider sx={{ mb: 1.5, borderColor: "#ff9a56" }} />
            <Typography variant="body2" sx={{ mb: 0.5, color: "#555" }}>
              💰 التكلفة السابقة: <strong>{defaults?.cost ?? "-"}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5, color: "#555" }}>
              📅 بداية سابقة: <strong>{defaults?.start_date}</strong>
            </Typography>
            <Typography variant="body2" sx={{ color: "#555" }}>
              📅 نهاية سابقة: <strong>{defaults?.end_date}</strong>
            </Typography>
          </Paper>
        )}

        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="معرف المستأجر"
            value={form.tenant_id}
            onChange={(e) => setForm((p) => ({ ...p, tenant_id: e.target.value }))}
            fullWidth
            disabled
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <TextField
            label="التكلفة"
            value={form.cost}
            onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <Stack direction="row" spacing={1.5}>
            <TextField
              label="تاريخ التجديد"
              type="date"
              value={form.renew_date}
              onChange={(e) => setForm((p) => ({ ...p, renew_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
                }
              }}
            />
            <TextField
              label="بداية الباقة"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
                }
              }}
            />
            <TextField
              label="نهاية الباقة"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
                }
              }}
            />
          </Stack>

          <TextField
            label="الموظف"
            value={form.employee_name}
            onChange={(e) => setForm((p) => ({ ...p, employee_name: e.target.value }))}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <TextField
            label="ملاحظات"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                background: "#fff",
                "&.Mui-focused fieldset": { borderColor: "#ff6a00" }
              }
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button 
              variant="outlined" 
              component="label" 
              size="medium"
              startIcon={<AttachFileIcon />}
              sx={{ 
                borderColor: "#ff6a00", 
                color: "#ff6a00",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { 
                  borderColor: "#ff6a00", 
                  background: "rgba(255, 106, 0, 0.05)" 
                }
              }}
            >
              رفع ملف
              <input type="file" hidden onChange={handleChange("file")} />
            </Button>
            {form.file && (
              <Typography variant="body2" sx={{ color: "#555", fontWeight: 600 }}>
                📎 {form.file.name}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, background: "#fafafa" }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ 
            textTransform: "none", 
            fontWeight: 600,
            color: "#666"
          }}
        >
          إلغاء
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? null : <SaveIcon />}
          sx={{ 
            background: "linear-gradient(135deg, #ff9a56 0%, #ff6a00 100%)",
            color: "#fff",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            "&:hover": {
              background: "linear-gradient(135deg, #ff8c43 0%, #e65c00 100%)",
            }
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "إرسال التجديد"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenewSystemDialog;
